import {
    kindIcon,
    listSpec,
    type Prefetch,
    useAuthStore,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    Badge,
    Button,
    DateTime,
    type ListColumnDef,
    type ListViewPreset,
    Money,
    ObjectRef,
    PageHeader,
    RowActions,
    StatusBadge,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { ChargebackRow, Dispute } from '@/api/types';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';
import { P } from '@/lib/permissions';
import { useList } from '@/lib/queries';
import { ADMIN_STATUSES } from '@/lib/statuses';
import {
    OpenDisputeSheet,
    OpenFromRowSheet,
} from '@/pages/disputes/open-dispute';

const PATH = '/staff/disputes';
const PAGE_KEY = 'disputes';

/** The columns, keyed like the export's columns (so Export writes what the table shows). */
const columns: ListColumnDef<Dispute>[] = [
    {
        key: 'id',
        label: 'Dispute',
        cell: (d) => <span className="font-mono text-xs">{d.id}</span>,
        csv: (d) => d.id,
        locked: 'start',
        noTruncate: true,
        boardCard: true,
    },
    {
        key: 'merchant_id',
        aliases: ['merchant'],
        label: 'Merchant',
        sortKey: 'merchant_id',
        cell: (d) => <ObjectRef id={d.merchant_id} />,
        csv: (d) => d.merchant_id,
    },
    {
        key: 'reason_code',
        aliases: ['source'],
        label: 'Reason',
        sortKey: 'reason_code',
        cell: (d) => (
            <span className="flex flex-wrap items-center gap-1">
                {d.reason_code ?? '—'}
                {d.over_disputed ? (
                    <StatusBadge
                        status="over_disputed"
                        domain={ADMIN_STATUSES.disputeFlag}
                    />
                ) : null}
            </span>
        ),
        csv: (d) => d.reason_code ?? '',
    },
    {
        key: 'amount_cents',
        aliases: ['amount'],
        label: 'Amount',
        sortKey: 'amount_cents',
        cell: (d) => <Money cents={d.amount_cents} />,
        csv: (d) => d.amount_cents,
        noTruncate: true,
        boardCard: true,
    },
    {
        key: 'fee_cents',
        aliases: ['fee'],
        label: 'Fee',
        sortKey: 'fee_cents',
        cell: (d) => <Money cents={d.fee_cents} />,
        csv: (d) => d.fee_cents,
        noTruncate: true,
    },
    {
        key: 'status',
        label: 'Status',
        sortKey: 'status',
        cell: (d) => <StatusBadge status={d.status} domain="dispute" />,
        csv: (d) => d.status,
        groupable: { key: 'status', statusDomain: 'dispute' },
    },
    {
        key: 'evidence_due_at',
        aliases: ['due'],
        label: 'Evidence due',
        kind: 'datetime',
        sortKey: 'evidence_due_at',
        cell: (d) => (
            <DateTime value={d.evidence_due_at} format="date" empty="—" />
        ),
        csv: (d) => d.evidence_due_at,
        noTruncate: true,
        boardCard: true,
    },
    {
        key: 'opened_at',
        label: 'Opened',
        kind: 'datetime',
        sortKey: 'opened_at',
        cell: (d) => <DateTime value={d.opened_at} format="date" empty="—" />,
        csv: (d) => d.opened_at,
        noTruncate: true,
        defaultHidden: true,
    },
];

const disputeFilters = filterDefs(PATH, {
    q: 'Search',
    status: 'Status',
    range: 'Opened',
});

/** BM-127: the processor's chargeback rows; the Unmatched tab waits for staff to pick the charge. */
const ROWS_PATH = '/staff/disputes/rows';
const ROWS_KEY = 'chargeback_rows';

const ROW_PRESETS: ListViewPreset[] = [
    { key: 'unmatched', name: 'Unmatched', filters: { state: ['unmatched'] } },
    { key: 'all', name: 'All rows', filters: {} },
];

const rowFilters = filterDefs(ROWS_PATH, {
    q: 'Search',
    state: 'State',
    range: 'Received',
});

function rowColumns(
    onOpen: ((r: ChargebackRow) => void) | null,
): ListColumnDef<ChargebackRow>[] {
    return [
        {
            key: 'provider_case_id',
            label: 'NMI chargeback',
            cell: (r) => (
                <span className="font-mono text-xs">{r.provider_case_id}</span>
            ),
            csv: (r) => r.provider_case_id,
            locked: 'start',
            noTruncate: true,
            boardCard: true,
        },
        {
            key: 'amount_cents',
            label: 'Amount',
            sortKey: 'amount_cents',
            cell: (r) => <Money cents={r.amount_cents} />,
            csv: (r) => r.amount_cents,
            noTruncate: true,
            boardCard: true,
        },
        {
            key: 'card',
            label: 'Card',
            cell: (r) => (
                <span className="font-mono text-xs">
                    {r.card_bin ?? '······'}…{r.card_last4 ?? '····'}
                </span>
            ),
            csv: (r) => `${r.card_bin ?? ''}…${r.card_last4 ?? ''}`,
            noTruncate: true,
        },
        {
            key: 'occurred_on',
            label: 'Chargeback date',
            kind: 'datetime',
            sortKey: 'occurred_on',
            cell: (r) => <DateTime value={r.occurred_on} format="date" />,
            csv: (r) => r.occurred_on,
            noTruncate: true,
        },
        {
            key: 'reason',
            label: 'Reason',
            cell: (r) => r.reason ?? '—',
            csv: (r) => r.reason ?? '',
        },
        {
            key: 'candidates',
            label: 'Matching charges',
            cell: (r) => String(r.candidates.length),
            csv: (r) => r.candidates.length,
            noTruncate: true,
        },
        {
            key: 'state',
            label: 'State',
            sortKey: 'state',
            cell: (r) => (
                <StatusBadge
                    status={r.state}
                    domain={ADMIN_STATUSES.chargebackRow}
                />
            ),
            csv: (r) => r.state,
            noTruncate: true,
        },
        {
            key: 'actions',
            label: 'Actions',
            locked: 'end',
            actions: true,
            // QA D3 (rule G.6): the row's action lives in the one "…" menu
            cell: (r) => (
                <RowActions
                    name={`Chargeback ${r.provider_case_id}`}
                    items={[
                        {
                            label: 'Open dispute…',
                            icon: kindIcon('dispute'),
                            hidden: !onOpen || r.state !== 'unmatched',
                            onSelect: () => onOpen?.(r),
                        },
                    ]}
                />
            ),
        },
    ];
}

function UnmatchedRows() {
    const can = useAuthStore((s) => s.can);
    const [opening, setOpening] = useState<ChargebackRow | null>(null);
    const onOpen = can(P.disputesRespond) ? setOpening : null;
    const { state, query } = useAdminList<ChargebackRow>(ROWS_KEY, ROWS_PATH, {
        columns: rowColumns(onOpen),
        filters: rowFilters,
        presets: ROW_PRESETS,
    });
    return (
        <>
            <AdminListPage
                noun="chargeback rows"
                state={state}
                query={query}
                rowKey={(r) => r.id}
                onRow={(r) =>
                    onOpen && r.state === 'unmatched' ? onOpen(r) : undefined
                }
                exportable={false}
                controls={{ q: { kind: 'search' } }}
            />
            {opening ? (
                <OpenFromRowSheet
                    row={opening}
                    onClose={() => setOpening(null)}
                />
            ) : null}
        </>
    );
}

/** A count, grouped ("1,284"), as the kit's pager writes totals. */
const COUNT = new Intl.NumberFormat('en-US');

/** One row is enough: the count is the list's total. */
const UNMATCHED_COUNT = {
    path: '/staff/disputes/rows',
    params: { state: ['unmatched'], per_page: 1 },
} as const;

const listPrefetch = adminListPrefetch(PAGE_KEY, PATH);

/** The route's prefetch: the person's views, then the rows of the view they land on (BM-46 §2.0 + §2.4), and the
 * Unmatched tab's count, so the server renders the tab with its badge and nothing moves after hydration (rule 1). */
export const disputesPrefetch: Prefetch = (context) => [
    ...listPrefetch(context),
    // never a read the person may not make (no 403 to log)
    ...(context.can(P.disputesRead)
        ? [listSpec(UNMATCHED_COUNT.path, UNMATCHED_COUNT.params)]
        : []),
];

function DisputesList() {
    const { state, query } = useAdminList<Dispute>(PAGE_KEY, PATH, {
        columns,
        filters: disputeFilters,
    });
    return (
        <>
            <AsOfBadge asOf={query.freshness.asOf} />
            <AdminListPage
                noun="disputes"
                state={state}
                query={query}
                rowKey={(d) => d.id}
                rowHref={(d) => `/disputes/${d.id}`}
                controls={{
                    q: { kind: 'search' },
                    status: { domain: 'dispute' },
                }}
            />
        </>
    );
}

export function DisputesPage() {
    const can = useAuthStore((s) => s.can);
    const [search, setSearch] = useSearchParams();
    const [opening, setOpening] = useState(false);
    const tab = search.get('tab') === 'unmatched' ? 'unmatched' : 'disputes';
    // design 1.5.1 (bm-127-design-v1.5.1 § 4): the rows waiting for staff, counted by the list's own total
    // (X-Total-Count); refreshed by the open-from-row write, which invalidates /staff/disputes*
    const unmatched = useList<ChargebackRow>(
        UNMATCHED_COUNT.path,
        UNMATCHED_COUNT.params,
    );
    const waiting = unmatched.data?.data.total ?? 0;
    return (
        <div>
            <PageHeader
                title="Disputes"
                meta="Chargebacks; the amount and the fee are charged to the merchant."
                primaryAction={
                    can(P.disputesRespond) ? (
                        <Button size="sm" onClick={() => setOpening(true)}>
                            Open dispute
                        </Button>
                    ) : undefined
                }
            />
            {/* each tab is its own list with its own URL state: switching starts the other list afresh */}
            <Tabs
                value={tab}
                onValueChange={(v) =>
                    setSearch(v === 'unmatched' ? { tab: 'unmatched' } : {})
                }
            >
                <TabsList>
                    <TabsTrigger value="disputes">Disputes</TabsTrigger>
                    <TabsTrigger value="unmatched">
                        Unmatched
                        {waiting > 0 ? (
                            <Badge
                                variant="neutral"
                                size="sm"
                                className="ml-1.5"
                                data-testid="unmatched-count"
                            >
                                {COUNT.format(waiting)}
                                {unmatched.data?.data.capped ? '+' : ''}
                            </Badge>
                        ) : null}
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="disputes" className="pt-2">
                    <DisputesList />
                </TabsContent>
                <TabsContent value="unmatched" className="pt-2">
                    <UnmatchedRows />
                </TabsContent>
            </Tabs>
            {opening ? (
                <OpenDisputeSheet onClose={() => setOpening(false)} />
            ) : null}
        </div>
    );
}
